import {
  ApiError,
  NETWORK_ERROR_CODE,
  UNEXPECTED_ERROR_CODE,
} from './ApiError';

/**
 * Jedyna droga do API. Nikt nie woła `fetch` bezpośrednio — inaczej obsługa
 * sesji, tenanta i błędów rozjedzie się na kilkanaście miejsc.
 *
 * Trzy rzeczy, które dzieją się tu i nigdzie indziej:
 *  1. `credentials: 'include'` — sesja jedzie ciasteczkiem związanym z tenantem
 *     (`TenantSessionGuard`, faza A3). Front i API stoją pod jednym pochodzeniem
 *     (decyzja 69), więc to zwykłe ciasteczko `SameSite=Lax`, bez CORS-u.
 *  2. `ProblemDetails` → `ApiError` z kluczem i18n i argumentami.
 *  3. Zgłoszenie utraty sesji jednym kanałem, żeby router mógł przekierować
 *     na logowanie z zapamiętanym adresem powrotu (spec 4A).
 */

const BASE = '/api';

/**
 * Kod, którym serwer mówi „konto firmy jest wstrzymane" (dec. 143).
 * ⚠ Ten sam napis stoi po stronie API — zmiana w jednym miejscu bez drugiego znaczy
 * pełnoekranowy komunikat, który przestaje się pokazywać.
 */
export const TENANT_SUSPENDED_CODE = 'tenant.suspended';

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
const suspendedListeners = new Set<Listener>();

/**
 * Nasłuch „sesja przepadła". Świadomie zdarzenie, a nie zależność od routera:
 * `httpClient` leży w `shared/` i nie wolno mu wiedzieć o nawigacji ani o tym,
 * że w ogóle istnieje ekran logowania.
 */
export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

/**
 * Nasłuch „firma wstrzymana". Osobny od utraty sesji, bo prowadzi gdzie indziej:
 * 401 wysyła na logowanie, a tu logowanie niczego nie naprawi — ⭐ to jest stan konta
 * firmy, nie użytkownika, i jedynym wyjściem jest kontakt z nami.
 */
export function onTenantSuspended(listener: Listener): () => void {
  suspendedListeners.add(listener);
  return () => suspendedListeners.delete(listener);
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;

  /**
   * Ciało wysyłane **bez kodowania** — plik, `Blob`, `FormData`.
   *
   * ⭐ Istnieje dla jednej rzeczy: **wgrania wsadu importu**. Pliki mają być większe niż
   * 100 MB (decyzja 107), a `JSON.stringify(await file.text())` wciągnąłby całość do pamięci
   * przeglądarki i zniweczył strumieniowanie, które po stronie serwera kosztowało osobny
   * parser. `fetch` z obiektem `File` przesyła go strumieniem.
   *
   * ⚠ **Nagłówka `Content-Type` NIE ustawiamy** — przy `FormData` przeglądarka musi dopisać
   * do niego granicę sekcji, a ustawiony ręcznie nagłówek jej nie ma i serwer nie rozłoży
   * multiparta.
   */
  rawBody?: BodyInit;

  signal?: AbortSignal;
  /**
   * Wyłącza zgłoszenie utraty sesji. Potrzebne dokładnie w dwóch miejscach:
   * przy sprawdzaniu „kim jestem" na starcie i przy logowaniu — tam 401 jest
   * **normalną odpowiedzią**, a nie utratą sesji, i przekierowanie na logowanie
   * z ekranu logowania byłoby pętlą.
   */
  anonymous?: boolean;
}

async function toApiError(response: Response): Promise<ApiError> {
  try {
    const problem: unknown = await response.json();

    if (problem && typeof problem === 'object') {
      const record = problem as Record<string, unknown>;
      const code =
        typeof record['code'] === 'string'
          ? record['code']
          : typeof record['title'] === 'string'
            ? record['title']
            : UNEXPECTED_ERROR_CODE;
      const args =
        record['args'] && typeof record['args'] === 'object'
          ? (record['args'] as Record<string, unknown>)
          : {};

      return new ApiError(response.status, code, args);
    }
  } catch {
    // Odpowiedź bez treści albo nie-JSON. Najczęstszy przypadek: 502 z proxy
    // albo fallback SPA, który złapał ścieżkę API — dlatego w Caddy `try_files`
    // NIE obejmuje `/api/*` (wdrozenie.md §9A.1).
  }

  return new ApiError(response.status, UNEXPECTED_ERROR_CODE);
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, rawBody, signal, anonymous = false } = options;

  // ⚠ Pola dokładane warunkowo, a nie ustawiane na `undefined`.
  // Przy `exactOptionalPropertyTypes` (tsconfig) „pole nieustawione" i „pole
  // ustawione na nic" to dwie różne rzeczy — i dobrze, bo `fetch` traktuje
  // jawne `body: undefined` inaczej niż brak pola.
  const init: RequestInit = { method, credentials: 'include' };

  if (rawBody !== undefined) {
    init.body = rawBody;
  } else if (body !== undefined) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body);
  }

  if (signal) init.signal = signal;

  let response: Response;

  try {
    response = await fetch(`${BASE}${path}`, init);
  } catch (cause) {
    // Przerwanie żądania to nie awaria — TanStack Query robi to przy zmianie
    // ekranu i nie chcemy z tego powodu pokazywać komunikatu o braku sieci.
    //
    // ⚠ Rozpoznajemy po NAZWIE i po stanie sygnału, nie po klasie. Przeglądarka
    // rzuca `DOMException`, ale silnik Node (i jsdom) opakowuje to inaczej —
    // warunek `instanceof DOMException` przechodziłby w przeglądarce i zawodził
    // wszędzie indziej, czyli błąd byłby niewidoczny dokładnie tam, gdzie
    // najłatwiej go złapać. Znalezione testem.
    if (signal?.aborted || (cause instanceof Error && cause.name === 'AbortError')) {
      throw cause;
    }

    throw new ApiError(0, NETWORK_ERROR_CODE);
  }

  if (response.status === 401 && !anonymous) {
    unauthorizedListeners.forEach((listener) => listener());
  }

  if (!response.ok) {
    const error = await toApiError(response);

    // ⚠ Sygnał wysyłamy PRZED rzuceniem: pełnoekranowy komunikat ma się pokazać także
    //   wtedy, gdy wołający połknie błąd (a większość zapytań tła go połyka).
    if (response.status === 403 && error.code === TENANT_SUSPENDED_CODE) {
      suspendedListeners.forEach((listener) => listener());
    }

    throw error;
  }

  // 204 i 202 nie mają treści — `response.json()` wywaliłby się na pustce.
  if (response.status === 204 || response.status === 202) return undefined as T;

  return (await response.json()) as T;
}

export const httpClient = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', ...(body === undefined ? {} : { body }) }),

  /** Wysyła plik strumieniem — patrz `RequestOptions.rawBody`. */
  upload: <T>(path: string, file: BodyInit, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', rawBody: file }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', ...(body === undefined ? {} : { body }) }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', ...(body === undefined ? {} : { body }) }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
